import { useState, useRef, useEffect } from 'react'
import { FiSearch, FiChevronDown } from 'react-icons/fi'
import { CURRENCIES, POPULAR_CURRENCIES, getCurrencyByCode, getFlagUrl, type Currency } from '@/data/currencies'

interface CurrencyDropdownProps {
  value: string
  onChange: (currencyCode: string) => void
  excludeCurrency?: string // Currency to exclude from the list
  className?: string
}

export default function CurrencyDropdown({
  value,
  onChange,
  excludeCurrency,
  className = ''
}: CurrencyDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedCurrency = getCurrencyByCode(value)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Filter currencies based on search and exclusion
  const getFilteredCurrencies = () => {
    const query = searchQuery.toLowerCase().trim()
    let filtered = CURRENCIES.filter(c => c.code !== excludeCurrency)

    if (query) {
      filtered = filtered.filter(
        c =>
          c.code.toLowerCase().includes(query) ||
          c.name.toLowerCase().includes(query)
      )
    }

    return filtered
  }

  const filteredCurrencies = getFilteredCurrencies()
  const popularCurrencies = filteredCurrencies.filter(c => POPULAR_CURRENCIES.includes(c.code))
  const otherCurrencies = filteredCurrencies.filter(c => !POPULAR_CURRENCIES.includes(c.code))

  const handleSelect = (currencyCode: string) => {
    onChange(currencyCode)
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
        style={{
          fontFamily: 'Lexend Deca',
          fontWeight: 400,
          fontSize: '14px',
        }}
      >
        {selectedCurrency && (
          <>
            <img
              src={getFlagUrl(selectedCurrency.countryCode, 'w20')}
              alt={selectedCurrency.name}
              className="w-5 h-4 object-cover rounded border border-gray-200"
              onError={(e) => {
                // Fallback to emoji if image fails to load
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                if (!target.nextElementSibling) {
                  const emoji = document.createElement('span')
                  emoji.textContent = selectedCurrency.countryCode === 'EU' ? '🇪🇺' : String.fromCodePoint(
                    ...selectedCurrency.countryCode
                      .toUpperCase()
                      .split('')
                      .map(char => 127397 + char.charCodeAt(0))
                  )
                  emoji.className = 'text-lg'
                  target.parentElement?.insertBefore(emoji, target)
                }
              }}
            />
            <span>{selectedCurrency.code}</span>
          </>
        )}
        <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50"
          style={{
            width: '320px',
            maxHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search Bar */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search currencies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                }}
              />
            </div>
          </div>

          {/* Currency List */}
          <div className="overflow-y-auto flex-1" style={{ maxHeight: '340px' }}>
            {/* Popular Currencies */}
            {popularCurrencies.length > 0 && (
              <div>
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Popular
                </div>
                {popularCurrencies.map((currency) => (
                  <CurrencyItem
                    key={currency.code}
                    currency={currency}
                    isSelected={currency.code === value}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}

            {/* All Currencies */}
            {otherCurrencies.length > 0 && (
              <div>
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  All Currencies
                </div>
                {otherCurrencies.map((currency) => (
                  <CurrencyItem
                    key={currency.code}
                    currency={currency}
                    isSelected={currency.code === value}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}

            {/* No Results */}
            {filteredCurrencies.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                No currencies found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface CurrencyItemProps {
  currency: Currency
  isSelected: boolean
  onSelect: (code: string) => void
}

function CurrencyItem({ currency, isSelected, onSelect }: CurrencyItemProps) {
  const [imgError, setImgError] = useState(false)

  return (
    <button
      type="button"
      onClick={() => onSelect(currency.code)}
      className={`w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-blue-50 transition-colors ${
        isSelected ? 'bg-blue-100' : ''
      }`}
    >
      {/* Flag */}
      <div className="w-6 h-4 flex-shrink-0 rounded overflow-hidden border border-gray-200">
        {!imgError ? (
          <img
            src={getFlagUrl(currency.countryCode, 'w40')}
            alt={currency.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs">
            {currency.countryCode === 'EU' ? '🇪🇺' : String.fromCodePoint(
              ...currency.countryCode
                .toUpperCase()
                .split('')
                .map(char => 127397 + char.charCodeAt(0))
            )}
          </div>
        )}
      </div>

      {/* Currency Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="font-medium text-gray-900"
            style={{
              fontFamily: 'Lexend Deca',
              fontSize: '14px',
            }}
          >
            {currency.code}
          </span>
        </div>
        <span
          className="text-xs text-gray-500 truncate block"
          style={{
            fontFamily: 'Lexend Deca',
          }}
        >
          {currency.name}
        </span>
      </div>
    </button>
  )
}
